/**
 * Practice Tab — Student workspace Practice view
 * Static 12-tool grid + timer bar. No BFF data dependency.
 * Extracted from StudentDashboardBeta (Phase 3.3d).
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../../i18n/TranslationProvider'

// ═══════════════════════════════════════════════════════════
// SectionHead — reused from LessonsTab (same definition)
// ═══════════════════════════════════════════════════════════

const SectionHead: React.FC<{
  title: string
  action?: { label: string; onClick: () => void }
}> = ({ title, action }) => (
  <div className="flex items-center justify-between mt-[24px] mb-[14px]">
    <h2 className="text-[15px] font-bold text-[var(--color-text-primary)]">{title}</h2>
    {action && (
      <button onClick={action.onClick}
              className="text-[12px] font-semibold text-[var(--color-teal)] hover:opacity-75 cursor-pointer">
        {action.label} →
      </button>
    )}
  </div>
)

// ═══════════════════════════════════════════════════════════
// Practice Tools (12-tool grid)
// ═══════════════════════════════════════════════════════════

const practiceTools = [
  { icon: '⏱', label: 'Timer', desc: 'Countdown & stopwatch', color: 'var(--color-neon)', route: '/tool/timer' },
  { icon: '🎵', label: 'Metronome', desc: 'Tap tempo · BPM', color: 'var(--color-amber-accent)', route: '/tool/metronome' },
  { icon: '📄', label: 'Sheet Music', desc: 'View & annotate', color: 'var(--color-cyan)', route: '/dashboard/materials' },
  { icon: '👂', label: 'Ear Training', desc: 'Intervals & chords', color: 'var(--color-jade)', route: '/tool/ear' },
  { icon: '🎙', label: 'Recorder', desc: 'Record & playback', color: 'var(--color-rose-accent)', route: '/tool/recorder' },
  { icon: '⭐', label: 'Tuner', desc: 'Tune instrument', color: 'var(--color-blue)', route: '/tool/tuner' },
  { icon: '🎼', label: 'Scales', desc: 'Interactive viewer', color: 'var(--color-gold)', route: '/tool/scales' },
  { icon: '🎹', label: 'Chord Builder', desc: 'Interactive chords', color: 'var(--color-amber-accent)', route: '/tool/chords' },
  { icon: '🥁', label: 'Rhythm', desc: 'Tap-back patterns', color: 'var(--color-jade)', route: '/tool/rhythm' },
  { icon: '👁', label: 'Sight Reading', desc: 'Progressive exercises', color: 'var(--color-cyan)', route: '/tool/sightreading' },
  { icon: '✏️', label: 'Theory', desc: 'Interactive lessons', color: 'var(--color-blue)', route: '/tool/theory' },
  { icon: '🃏', label: 'Flashcards', desc: 'Notes & rhythm', color: 'var(--color-rose-accent)', route: '/tool/flashcards' },
] as const

// ═══════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════

const PracticeTab: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  return (
    <>
      {/* Timer bar — static placeholder */}
      <div className="sticky top-0 z-[50] bg-[var(--color-elevated)] backdrop-blur-[20px]
                      border border-[var(--color-border-light)] rounded-[var(--radius-r)]
                      p-[12px] flex items-center gap-[14px] shadow-[var(--shadow-sh3)] mb-[20px] flex-wrap">
        <span className="text-[28px] font-black tabular-nums" style={{ color: 'var(--color-neon)' }}>00:00</span>
        <span className="text-[12px] text-[var(--color-text-secondary)] flex-1">Ready to practice</span>
        <button className="px-[16px] py-[8px] bg-[var(--color-teal)] text-white text-[13px] font-semibold
                          rounded-[var(--radius-r-sm)] hover:opacity-90 transition-all duration-[.18s]">
          ▶ Start
        </button>
      </div>

      {/* Tools Grid */}
      <SectionHead title="Practice Tools" />
      <div className="grid grid-cols-[repeat(auto-fill,minmax(125px,1fr))] gap-[10px]">
        {practiceTools.map((tool, i) => (
          <div key={i}
               onClick={() => navigate(tool.route)}
               className="bg-[var(--color-card)] border border-[var(--color-border-light)]
                          rounded-[var(--radius-r)] p-[18px] text-center cursor-pointer
                          transition-all duration-[.18s] shadow-[var(--shadow-sh0)]
                          flex flex-col items-center gap-[6px]
                          hover:-translate-y-[3px] hover:shadow-[var(--shadow-sh2)]
                          hover:border-[var(--color-border)]">
            <span className="text-[26px]">{tool.icon}</span>
            <span className="text-[13px] font-semibold text-[var(--color-text-primary)]">{tool.label}</span>
            <span className="text-[10px] text-[var(--color-text-tertiary)]">{tool.desc}</span>
          </div>
        ))}
      </div>
    </>
  )
}

export default PracticeTab
